<script lang="ts">
    import {isStickyStuck, type IsStickyStuckEvent} from 'shared/svelte-actions';
    import type {CssSize} from 'shared/ui-widgets/types';
    import ResponsiveTableContainer from 'shared/ui-widgets/table/ResponsiveTableContainer.svelte';

    export let additionalClasses = '';
    export let fontSize: CssSize = '14px';
    export let fixedLayout = false;
    export let rowsTopBordered = false;
    export let cellHeight: CssSize = '38px';
    export let headerFooterDivided = false;
    export let colorAccented = false;
    export let responsive = false;
    export let stickyHeader = false;

    let isHeaderStuckToTop = false;

    const handleStickyChange = (event: IsStickyStuckEvent) => {
        isHeaderStuckToTop = event.detail.stuck;
    };
</script>

<ResponsiveTableContainer {responsive}>
    <table class="kp-barebones-table {additionalClasses}"
           style:--font-size={fontSize}
           style:--cell-height={cellHeight}
           class:fixed-layout={fixedLayout}
           class:header-footer-divided={headerFooterDivided}
           class:header-color-accented={colorAccented}
           class:rows-top-bordered={rowsTopBordered}>

        {#if $$slots.header}
            <thead class:sticky-header={stickyHeader}
                   class:is-stuck={isHeaderStuckToTop}
                   use:isStickyStuck
                   on:sticky-change={handleStickyChange}>

                <slot name="header"/>
            </thead>
        {/if}

        <tbody>
            <slot name="body"/>
        </tbody>

        {#if $$slots.footer}
            <tfoot>
                <slot name="footer"/>
            </tfoot>
        {/if}
    </table>
</ResponsiveTableContainer>

<style lang="less">
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.variables.less";

    .kp-barebones-table {
        width: 100%;
        border-collapse: collapse;
        word-break: break-word;
        font-size: var(--font-size);

        &.fixed-layout {
            table-layout: fixed;
        }
    }

    :global {
        .kp-barebones-table {
            thead > tr > th {
                font-weight: 500;
                padding-bottom: @spacing-s;
                white-space: nowrap;
            }

            &.rows-top-bordered tbody > tr {
                border-top: 1px solid @themed-border-light;
            }

            &.rows-top-bordered.header-footer-divided tbody > tr {
                &:first-child {
                    border-top: none;
                }
            }

            &.header-footer-divided {
                thead > tr > th {
                    padding-bottom: @spacing-m;
                    border-bottom: 1px solid @themed-border-light;
                }

                tfoot > tr > td {
                    padding-top: @spacing-m;
                    border-top: 1px solid @themed-border-light;
                }
            }

            &.header-color-accented {
                thead > tr > th {
                    color: var(--table-header-accent-color);
                    border-bottom: 2px solid var(--table-header-accent-color);
                }
            }

            thead,
            tbody,
            tfoot {
                & > tr > td,
                & > tr > th {
                    min-height: var(--cell-height);
                    vertical-align: middle;
                    padding-right: @spacing-m;
                    white-space: nowrap;

                    &:last-child {
                        padding-right: 0;
                        text-align: end;
                    }
                }

                & > tr > td {
                    padding-top: @spacing-sm;
                    padding-bottom: @spacing-sm;
                }

                & > tr > th {
                    padding-bottom: @spacing-m;
                }
            }

            tfoot > tr > td {
                padding-bottom: 0;
            }

            thead.sticky-header {
                background-color: @themed-body-bg;
                position: sticky;
                top: 0;
                z-index: 90;

                & > tr > th {
                    padding-top: @spacing-ml;
                    padding-bottom: @spacing-ml;
                }

                &.is-stuck > tr > th {
                    border-bottom: 1px solid @themed-border-light;
                }
            }
        }
    }
</style>